import { useCallback } from "react";
import useGeneration from "@/views/generation/hooks/useGeneration";
import { QUESTION_TYPE } from "@/utils/constants";
import { showSnackbar } from "@/utils/snackbar.helper";
import type {
  QuestionGroup,
  SubQuestion,
  AnalysisGroup,
  SubAnalysis,
} from "@/types/apis/naniexpert";
import {
  StyledContainer,
  StyledHeader,
  StyledTitle,
  StyledHeaderButtons,
  StyledSecondaryButton,
  StyledContentWrapper,
  StyledSectionContainer,
  StyledCategoryTitle,
  StyledGroupedContentContainer,
  StyledContent,
  StyledOptionsList,
  StyledPrimaryButton,
  StyledGroupedContent,
  StyledDivider,
  StyledQuestionList,
  StyledQuestionContent,
} from "./GenerationResult.styles";
import { Divider } from "@mui/material";
import {
  buildAnalysisText,
  buildQuestionText,
} from "@/utils/generation.helper";
import useNavigationBlocker from "@/hooks/useNavigationBlocker";

const SubQuestionContent = ({ subQuestion }: { subQuestion: SubQuestion }) => (
  <StyledQuestionContent>
    <StyledContent>{subQuestion.questionContent}</StyledContent>
    {subQuestion.type === QUESTION_TYPE.MULTIPLE_CHOICE &&
      subQuestion.options.length > 0 && (
        <StyledOptionsList>
          {subQuestion.options.map((opt, i) => (
            <StyledContent key={i}>{opt}</StyledContent>
          ))}
        </StyledOptionsList>
      )}
  </StyledQuestionContent>
);

const QuestionGroupContent = ({ group }: { group: QuestionGroup }) => (
  <StyledGroupedContent>
    <StyledContent>{group.groupTitle}</StyledContent>
    {group.readingText && <StyledContent>{group.readingText}</StyledContent>}
    <StyledQuestionList>
      {group.subQuestions.map((sq, index) => (
        <SubQuestionContent key={index} subQuestion={sq} />
      ))}
    </StyledQuestionList>
  </StyledGroupedContent>
);

const SubAnalysisContent = ({ subAnalysis }: { subAnalysis: SubAnalysis }) => (
  <StyledQuestionContent>
    <StyledContent>{subAnalysis.questionContent}</StyledContent>
    {subAnalysis.correctAnswer && (
      <StyledContent>{subAnalysis.correctAnswer}</StyledContent>
    )}
    <StyledContent>{subAnalysis.analysis}</StyledContent>
  </StyledQuestionContent>
);

const AnalysisGroupContent = ({ group }: { group: AnalysisGroup }) => (
  <StyledGroupedContent>
    <StyledContent>{group.groupTitle}</StyledContent>
    <StyledQuestionList>
      {group.subAnalysises.map((sa, index) => (
        <SubAnalysisContent key={index} subAnalysis={sa} />
      ))}
    </StyledQuestionList>
  </StyledGroupedContent>
);

const GenerationResult = () => {
  const { handleGenerate, generationResult } = useGeneration();
  useNavigationBlocker(true);

  const handleSelectAgain = useCallback(() => {
    window.open("/", "_blank");
  }, []);

  const handleCopyQuestion = useCallback(() => {
    if (!generationResult?.questionGroups?.length) return;
    const text = generationResult.questionGroups
      .map(buildQuestionText)
      .join("\n\n");
    navigator.clipboard.writeText(text).then(() => {
      showSnackbar("已複製內容！");
    });
  }, [generationResult]);

  const handleCopyAnalysis = useCallback(() => {
    if (!generationResult?.analysisGroups?.length) return;
    const text = generationResult.analysisGroups
      .map(buildAnalysisText)
      .join("\n\n");
    navigator.clipboard.writeText(text).then(() => {
      showSnackbar("已複製內容！");
    });
  }, [generationResult]);

  if (!generationResult?.questionGroups?.length) {
    return (
      <StyledContainer>
        <StyledTitle>尚無生成結果</StyledTitle>
      </StyledContainer>
    );
  }

  return (
    <StyledContainer>
      <StyledHeader>
        <StyledTitle>大功告成! 以下是我為您準備的結果:</StyledTitle>
        <StyledHeaderButtons>
          <StyledSecondaryButton onClick={handleGenerate}>
            再次生成
          </StyledSecondaryButton>
          <StyledSecondaryButton onClick={handleSelectAgain}>
            重選範圍出題
          </StyledSecondaryButton>
        </StyledHeaderButtons>
      </StyledHeader>

      <StyledContentWrapper>
        <StyledSectionContainer>
          <StyledCategoryTitle>題目：</StyledCategoryTitle>
          <StyledGroupedContentContainer>
            {generationResult.questionGroups.map((group, groupIndex) => (
              <div key={groupIndex}>
                <QuestionGroupContent group={group} />
                {groupIndex < generationResult.questionGroups.length - 1 && (
                  <StyledDivider />
                )}
              </div>
            ))}
          </StyledGroupedContentContainer>
          <StyledPrimaryButton
            startIcon={<img src="/generationPage/copy.svg" alt="copy" />}
            onClick={handleCopyQuestion}
          >
            複製題目
          </StyledPrimaryButton>
        </StyledSectionContainer>
        <Divider orientation="vertical" flexItem />
        <StyledSectionContainer>
          <StyledCategoryTitle>解析：</StyledCategoryTitle>
          <StyledGroupedContentContainer>
            {generationResult.analysisGroups.map((group, groupIndex) => (
              <div key={groupIndex}>
                <AnalysisGroupContent group={group} />
                {groupIndex < generationResult.analysisGroups.length - 1 && (
                  <StyledDivider />
                )}
              </div>
            ))}
          </StyledGroupedContentContainer>
          <StyledPrimaryButton
            startIcon={<img src="/generationPage/copy.svg" alt="copy" />}
            onClick={handleCopyAnalysis}
          >
            複製解析
          </StyledPrimaryButton>
        </StyledSectionContainer>
      </StyledContentWrapper>
    </StyledContainer>
  );
};

export default GenerationResult;
